/* eslint-disable unicode-bom */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

function SafetyReportSectionSelectModal({ show, onClose, project, onConfirm, isLoading = false }) {
  const [sections, setSections] = useState({
    cover: true,
    prep: true,
    evals: true,
    photos: true,
    conclusion: true,
    meetings: true,
    trainings: true
  });

  // 모달이 열릴 때마다 체크박스 상태 초기화
  useEffect(() => {
    if (show) {
      setSections({
        cover: true,
        prep: true,
        evals: true,
        photos: true,
        conclusion: true,
        meetings: true,
        trainings: true
      });
    }
  }, [show]);

  if (!show || !project) return null;

  const handleToggle = (key) => {
    setSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSelectAll = (val) => {
    setSections({
      cover: val,
      prep: val,
      evals: val,
      photos: val,
      conclusion: val,
      meetings: val,
      trainings: val
    });
  };

  const isAllChecked = Object.values(sections).every(v => v);

  return createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 999998,
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '520px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', maxHeight: '90vh', position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* 로딩 표시 */}
        {isLoading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.9)', zIndex: 9999,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
          }}>
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}></div>
            <h5 className="fw-bold text-dark">데이터를 로드 중입니다...</h5>
          </div>
        )}

        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
          <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
            <i className="fas fa-file-pdf me-2 text-primary"></i>
            종합보고서 인쇄 항목 선택
          </h4>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#64748b', cursor: 'pointer', padding: 0, lineHeight: 1 }}>&times;</button>
        </div>

        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <div style={{ marginBottom: '20px', color: '#475569', fontSize: '0.9rem', lineHeight: '1.5' }}>
            <strong>[{project.name || '프로젝트'}]</strong> 종합보고서 출력 시 인쇄할 세부 항목들을 선택해 주세요.
          </div>

          <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', backgroundColor: '#f8fafc' }}>
            {/* 전체 선택 */}
            <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bold', color: '#0f172a', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  style={{ width: '17px', height: '17px' }}
                  checked={isAllChecked}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                전체 항목 인쇄
              </label>
            </div>

            {/* 개별 선택 리스트 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingLeft: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#334155', cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={sections.cover}
                  onChange={() => handleToggle('cover')}
                />
                1. 표지 (결재선 포함)
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#334155', cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={sections.prep}
                  onChange={() => handleToggle('prep')}
                />
                2. 사전준비 및 유해위험 파악 매트릭스
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#334155', cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={sections.evals}
                  onChange={() => handleToggle('evals')}
                />
                3. 위험성 결정 및 감소대책 수립 평가표
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#334155', cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={sections.photos}
                  onChange={() => handleToggle('photos')}
                />
                4. 개선 결과 (조치 전/후 사진대지)
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#334155', cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={sections.conclusion}
                  onChange={() => handleToggle('conclusion')}
                />
                5. 위험성평가 종합 총평
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#334155', cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={sections.meetings}
                  onChange={() => handleToggle('meetings')}
                />
                6. 위험성평가 회의록
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#334155', cursor: 'pointer', userSelect: 'none', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={sections.trainings}
                  onChange={() => handleToggle('trainings')}
                />
                7. 근로자 안전보건교육 일지
              </label>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#f8fafc' }}>
          <button className="btn btn-secondary" style={{ borderRadius: '8px', fontWeight: 600, padding: '8px 20px', fontSize: '0.9rem' }} onClick={onClose}>닫기</button>
          <button
            className="btn btn-primary"
            style={{ borderRadius: '8px', fontWeight: 600, padding: '8px 24px', fontSize: '0.9rem' }}
            onClick={() => onConfirm(sections)}
            disabled={!Object.values(sections).some(v => v)}
          >
            <i className="fas fa-print me-2"></i>인쇄 화면 보기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default SafetyReportSectionSelectModal;
